import './sales_volume.css'
import WeekendIcon from '@assets/CustomIcons/summer.svg'
import { Horizontal, Texto, useThemeContext, Vertical } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { numberCommaFormatter } from '@utils/intl/formatters'
import { Avatar, Col, Row, Select, Spin } from 'antd'
import axios from 'axios'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import { dateFormat } from '@constants/dateFormat'

const { Option } = Select

const volumeTypes = {
  DAILY: 'DATE',
  HOURLY: 'TIME',
}

const weeksRange = [1, 2, 3]
const hoursRange = [6, 12, 24]

function LegendColor({ width, height, color, name }) {
  return (
    <Horizontal gap='4px' alignItems='center' className='forecasts-legend-row'>
      <div style={{ width, height, backgroundColor: color, borderRadius: '100%' }} />
      <Texto>{name}</Texto>
    </Horizontal>
  )
}

export function SalesVolume({ selectedTank, selectedManifold, store_number, data, setData, rundownToggled }) {
  const [volumeTitle, setVolumeTitle] = useState(volumeTypes.DAILY)

  const [actualRange, setActualRange] = useState(1)
  const [forecastRange, setForecastRange] = useState(1)
  const [actualsLoading, setActualsLoading] = useState(false)
  const [forecastLoading, setForecastLoading] = useState(false)
  const [cancelToken, setCancelToken] = useState()

  const legendHeaderSpring = useSpring({
    to: { opacity: 1, x: 0, display: 'block' },
    from: { opacity: 0, x: -20, display: 'none', width: '100%' },
    reset: data?.length && rundownToggled,
    reverse: !rundownToggled,
  })

  const defaultVolumeHeaderSpring = useSpring({
    to: { opacity: 1, x: 0, display: 'block' },
    from: { opacity: 0, x: 20, display: 'none', width: '100%' },
    reset: data?.length && !rundownToggled,
    reverse: rundownToggled,
  })

  useEffect(() => {
    if (!selectedManifold && !selectedTank) return
    const multiplier = volumeTitle === volumeTypes.DAILY ? 7 : 1
    const filters = {
      actual: actualRange * multiplier,
      forecast: forecastRange * multiplier,
    }
    const newToken = axios.CancelToken.source()
    if (cancelToken) {
      cancelToken.cancel()
    }

    setCancelToken(newToken)
    api
      .getStoreForecast(
        store_number,
        selectedManifold ? [selectedManifold.manifold_id] : [selectedTank.tank_id],
        filters
      )
      .then((resp) => {
        setData(resp.data)
        setActualsLoading(false)
        setForecastLoading(false)
        setForecastWithDropsLoading(false)
      })
      .catch((e) => console.log('request canceled'))
  }, [selectedManifold, selectedTank, actualRange, forecastRange])

  if ((!selectedTank && !selectedManifold) || !data || data.length === 0)
    return (
      <Col span={8} align='center'>
        <Spin />
      </Col>
    )

  const isDaily = volumeTitle === volumeTypes.DAILY
  const time = isDaily ? 'Weeks' : 'Hours'
  const actualsLabel = isDaily ? 'FORECAST' : ''

  const volumeData = isDaily ? data.daily_actuals : data.hourly_actuals
  const forecastData = isDaily ? data.daily_forecast : data.hourly_forecast
  const projectedData = isDaily ? data.daily_forecast_with_drops : data.hourly_forecast_with_drops

  // Merge forecastData and projectedData
  const aggregatedForecastData = projectedData?.reduce(
    (acc, item, index) => [...acc, { projected: item, actual: forecastData[index] }],
    []
  )

  const ranges = isDaily ? weeksRange : hoursRange

  const volumeChange = (value) => {
    if (value === volumeTypes.DAILY) {
      setVolumeTitle(volumeTypes.DAILY)
      setActualRange(1)
      setForecastRange(1)
      setForecastWithDropsRange(1)
    } else {
      setVolumeTitle(volumeTypes.HOURLY)
      setActualRange(6)
      setForecastRange(6)
      setForecastWithDropsRange(6)
    }

    setActualsLoading(true)
    setForecastLoading(true)
    setForecastWithDropsLoading(true)
  }

  return (
    <Col span={8} className='pl-5'>
      <SalesVolumeHeader volumeChange={volumeChange} />
      <div className='sales-volume-container vertical-flex-div' style={{ gap: '2rem' }}>
        <div>
          <SalesVolumeDateSelectRow
            rowTitle='ACTUALS'
            rangeTitle={`${time} Before`}
            ranges={ranges}
            range={actualRange}
            onChange={setActualRange}
            setLoading={setActualsLoading}
            loading={actualsLoading}
          />
          <VolumeTitleRow title={volumeTitle} label={actualsLabel} />
          <div className='sales-volume-section-container'>
            {volumeData.map((v, i) => (
              <SalesVolumeRow key={i} volume={v} isDaily={isDaily} />
            ))}
          </div>
        </div>
        <div>
          <SalesVolumeDateSelectRow
            rowTitle='FORECASTS'
            rangeTitle={`${time} Forward`}
            ranges={ranges}
            range={forecastRange}
            onChange={setForecastRange}
            setLoading={setForecastLoading}
            loading={forecastLoading}
          />

          <VolumeTitleRow
            title={volumeTitle}
            label='INVENTORY'
            isForecast
            rundownToggled={rundownToggled}
            springs={{ legend: legendHeaderSpring, default: defaultVolumeHeaderSpring }}
          />

          <div className='sales-volume-section-container'>
            {aggregatedForecastData?.map((a, i) => (
              <ForecastVolumeRow
                key={i}
                aggregate={a}
                isDaily={isDaily}
                dataLoaded={data?.length}
                rundownToggled={rundownToggled}
                index={i}
              />
            ))}
          </div>
        </div>
      </div>
    </Col>
  )
}

function SalesVolumeHeader({ volumeChange }) {
  return (
    <Row className='grid-header-control py-3 pt-4' style={{ minHeight: 45 }}>
      <Col className='grid-header mb-0 vertical-flex-center' span={12}>
        <Texto category='heading-small'>Sales Volumes</Texto>
      </Col>
      <Col align='right' span={12}>
        <Select
          defaultValue={volumeTypes.DAILY}
          style={{ width: 85 }}
          className='sales-volume-select'
          onChange={volumeChange}
        >
          <Option value={volumeTypes.DAILY}>Daily</Option>
          <Option value={volumeTypes.HOURLY}>Hourly</Option>
        </Select>
      </Col>
    </Row>
  )
}

function SalesVolumeDateSelectRow({ rowTitle, rangeTitle, ranges, range, onChange, setLoading, loading }) {
  return (
    <Row className='sales-volume-header-row'>
      <Col align='left' span={8}>
        {rowTitle}
      </Col>
      <DateChangeControls
        title={rangeTitle}
        ranges={ranges}
        range={range}
        onChange={onChange}
        setLoading={setLoading}
        loading={loading}
      />
    </Row>
  )
}

function DateChangeControls({ title, ranges, range, onChange, setLoading, loading }) {
  return (
    <Col span={16} align='right' className='date-change-column'>
      {loading && <Spin size='small' className='mr-3' />}
      <span className='date-change'>{title}</span>
      {ranges.map((r, i) => (
        <span
          key={i}
          onClick={() => {
            setLoading(true)
            onChange(r)
          }}
        >
          <Avatar size='small' className={r === range ? 'selected' : ''}>
            {r}
          </Avatar>
        </span>
      ))}
    </Col>
  )
}

function VolumeTitleRow({ title, label, isForecast = false, rundownToggled, springs }) {
  const separator = label !== '' ? '/' : ''
  const { isDarkTheme } = useThemeContext()

  const legendColors = [
    { name: 'Forecasts', color: 'var(--theme-optimal)' },
    { name: 'W/ Projected', color: 'var(--theme-color-2)' },
    { name: 'Sales', color: 'var(--theme-color-1)' },
  ]

  if (isDarkTheme) {
    legendColors[2].color = 'var(--theme-option)'
  }
  return (
    <Row className={isForecast ? 'sales-volume-title-row forecast' : 'sales-volume-title-row'}>
      {rundownToggled && isForecast && (
        <animated.div style={springs.legend}>
          <Row gutter={16} className='p-2 title-row-legend-items-spacing' justify='center'>
            {legendColors.map((item) => (
              <LegendColor key={item.color} width={16} height={16} {...item} />
            ))}
          </Row>
        </animated.div>
      )}
      {isForecast ? (
        <animated.div style={springs.default}>
          <Row className='p-2' justify='space-between'>
            <Col align='left' className='value'>
              {title}
            </Col>
            <Col align='right'>
              <span className='label pr-1'>{label}</span>
              {separator}
              <span className='sales pl-1'>SALES</span>
            </Col>
          </Row>
        </animated.div>
      ) : (
        <Horizontal style={{ width: '100%' }} justifyContent='space-between'>
          <Vertical>DATE</Vertical>
          <Vertical alignItems='flex-end'>INVENTORY / SALES</Vertical>
        </Horizontal>
      )}
    </Row>
  )
}

const getDisplayDate = (isDaily, date) => {
  const format = isDaily ? dateFormat.WEEKDAY_MONTH_DATE : dateFormat.WEEKDAY_MONTH_DATE_TIME
  return <ForecastDisplayDate date={date} format={format} />
}

const propertyOrNA = (raw) => (raw == null || raw < 0 ? 'N/A' : numberCommaFormatter.format(raw))

const AnimatedCol = animated(Col)

function ForecastVolumeRow({ aggregate, isDaily, index, dataLoaded, rundownToggled }) {
  const spring = useSpring({
    from: { x: -10, opacity: 0 },
    to: { x: 0, opacity: 1 },
    config: { duration: 200 + index * 50 },
    reset: dataLoaded && rundownToggled,
    reverse: !rundownToggled,
  })
  const displayDate = getDisplayDate(isDaily, aggregate.actual?.date)
  const { isDarkTheme } = useThemeContext()

  const legendColors = [
    { name: 'Forecasts', color: 'var(--theme-optimal)' },
    { name: 'W/ Projected', color: 'var(--theme-color-2)' },
    { name: 'Sales', color: 'var(--theme-color-1)' },
  ]

  if (isDarkTheme) {
    legendColors[2].color = 'var(--theme-option)'
  }
  return (
    <Row className='sales-volume-row p-3'>
      <Col className='value'>{displayDate}</Col>
      <Col flex={1} />
      <Col align='right' span={4}>
        <Texto align='right' weight='bold' appearance='optimal' className=' mr-3'>
          {propertyOrNA(aggregate?.actual?.inventory)}
        </Texto>
      </Col>
      {rundownToggled && (
        <AnimatedCol style={spring} align='right' span={4}>
          <Texto align='right' className='projected-forecast-value'>
            ({propertyOrNA(aggregate?.projected?.inventory)})
          </Texto>
        </AnimatedCol>
      )}

      <Col align='right' span={4}>
        <Texto align='right' className={isDarkTheme ? 'forecast-value-dark' : 'forecast-value'}>
          {propertyOrNA(rundownToggled ? aggregate?.projected?.forecast : aggregate?.actual?.forecast)}
        </Texto>
      </Col>
    </Row>
  )
}
function SalesVolumeRow({ volume, isDaily }) {
  const displayDate = getDisplayDate(isDaily, volume.date)
  const { isDarkTheme } = useThemeContext()
  return (
    <Row className='p-3 sales-volume-row'>
      <Col align='left' className='value' span={8}>
        {displayDate}
      </Col>
      <Col flex={1} />
      <Col align='right' span={6}>
        {isDaily && <span className='row-label mr-3 '>{propertyOrNA(volume?.forecast)}</span>}
      </Col>
      <Col align='right' span={6} className={isDarkTheme ? 'actuals-value-dark' : 'actuals-value'}>
        {propertyOrNA(volume?.sales)}
      </Col>
    </Row>
  )
}

function ForecastDisplayDate({ date, format }) {
  const forecastDate = moment(date)
  const day = forecastDate.format('dddd')
  const showIcon = day === 'Sunday' || day === 'Saturday'
  return (
    <b className={showIcon ? 'weekend-icon-container' : ''}>
      {forecastDate.format(format)} {showIcon ? <img alt='' className='weekend-icon' src={WeekendIcon} /> : ''}
    </b>
  )
}
